import type { Cents } from './money.ts';
import { isValidTaxBps } from './pricing.ts';

// Ventas: totales, IVA, descuentos, recargos/descuentos por medio de pago, redondeo en efectivo
// y devoluciones. Todo en centavos y milésimas enteras. El servidor (app.record_sale) vuelve a
// comprobar las mismas cuentas, así que cualquier cambio acá tiene que reflejarse allá.

// ---------------------------------------------------------------------------
// Medios de pago y configuración del punto de venta
// ---------------------------------------------------------------------------

// El orden es el de los botones de «Cobrar» (dos columnas) y el de las listas de medios:
// transferencia va al lado de efectivo porque es la más usada.
export const PAYMENT_METHODS = {
  cash: { label: 'Efectivo' },
  transfer: { label: 'Transferencia' },
  credit: { label: 'Crédito' },
  debit: { label: 'Débito' },
  mercado_pago: { label: 'Mercado Pago / QR' },
  account: { label: 'Cuenta corriente' },
} as const;

export type PaymentMethod = keyof typeof PAYMENT_METHODS;
export const PAYMENT_METHOD_KEYS = Object.keys(PAYMENT_METHODS) as PaymentMethod[];

export interface PaymentMethodSetting {
  enabled: boolean;
  /** Recargo (+) o descuento (−) en puntos básicos: −1000 = 10 % de descuento. */
  adjust_bps: number;
}

export type NegativeStockPolicy = 'warn' | 'block';

export interface PosSettings {
  payments: Record<PaymentMethod, PaymentMethodSetting>;
  /** Redondeo del cobro en efectivo, en centavos (0 = sin redondeo; 1000 = a $10). */
  cash_rounding: number;
  /** Vender sin stock: avisar y dejar vender, o no dejar. */
  negative_stock: NegativeStockPolicy;
  ticket_footer: string;
  ticket_width: 58 | 80;
  /** Imprimir el logo del negocio arriba del ticket (si tiene). */
  ticket_logo: boolean;
  /** Para vender hay que tener la caja abierta en el dispositivo. */
  require_open_cash: boolean;
  /** Los empleados cierran la caja sin ver cuánto debería haber (arqueo ciego). */
  blind_close: boolean;
}

export const CASH_ROUNDING_OPTIONS = [
  { cents: 0, label: 'Sin redondeo' },
  { cents: 1000, label: 'A $10' },
  { cents: 5000, label: 'A $50' },
  { cents: 10000, label: 'A $100' },
] as const;

/** Límites del recargo/descuento por medio de pago: −50 % a +100 %. */
export const MIN_ADJUST_BPS = -5000;
export const MAX_ADJUST_BPS = 10000;

export const DEFAULT_POS_SETTINGS: PosSettings = {
  payments: {
    cash: { enabled: true, adjust_bps: 0 },
    transfer: { enabled: true, adjust_bps: 0 },
    credit: { enabled: true, adjust_bps: 0 },
    debit: { enabled: true, adjust_bps: 0 },
    mercado_pago: { enabled: false, adjust_bps: 0 },
    account: { enabled: true, adjust_bps: 0 },
  },
  cash_rounding: 0,
  negative_stock: 'warn',
  ticket_footer: '¡Gracias por tu compra!',
  ticket_width: 80,
  ticket_logo: true,
  require_open_cash: true,
  blind_close: true,
};

/** Configuración guardada (jsonb del negocio) → configuración completa y válida. */
export function normalizePosSettings(raw: unknown): PosSettings {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const rawPayments = src.payments && typeof src.payments === 'object' ? (src.payments as Record<string, unknown>) : {};
  const payments = {} as PosSettings['payments'];
  for (const method of PAYMENT_METHOD_KEYS) {
    const def = DEFAULT_POS_SETTINGS.payments[method];
    const p = rawPayments[method] && typeof rawPayments[method] === 'object' ? (rawPayments[method] as Record<string, unknown>) : {};
    const bps = typeof p.adjust_bps === 'number' && Number.isSafeInteger(p.adjust_bps) ? p.adjust_bps : def.adjust_bps;
    payments[method] = {
      enabled: typeof p.enabled === 'boolean' ? p.enabled : def.enabled,
      adjust_bps: Math.min(MAX_ADJUST_BPS, Math.max(MIN_ADJUST_BPS, bps)),
    };
  }
  // Siempre queda al menos un medio habilitado (la cuenta corriente sola no alcanza: necesita un cliente).
  if (!PAYMENT_METHOD_KEYS.some((m) => m !== 'account' && payments[m].enabled)) payments.cash.enabled = true;
  const rounding = CASH_ROUNDING_OPTIONS.some((o) => o.cents === src.cash_rounding) ? (src.cash_rounding as number) : 0;
  return {
    payments,
    cash_rounding: rounding,
    negative_stock: src.negative_stock === 'block' ? 'block' : 'warn',
    ticket_footer: typeof src.ticket_footer === 'string' ? src.ticket_footer.slice(0, 200) : DEFAULT_POS_SETTINGS.ticket_footer,
    ticket_width: src.ticket_width === 58 ? 58 : 80,
    ticket_logo: typeof src.ticket_logo === 'boolean' ? src.ticket_logo : DEFAULT_POS_SETTINGS.ticket_logo,
    require_open_cash: typeof src.require_open_cash === 'boolean' ? src.require_open_cash : DEFAULT_POS_SETTINGS.require_open_cash,
    blind_close: typeof src.blind_close === 'boolean' ? src.blind_close : DEFAULT_POS_SETTINGS.blind_close,
  };
}

/** "−10 %" / "+15 %" / "" (sin ajuste). */
export function formatAdjust(bps: number): string {
  if (bps === 0) return '';
  const pct = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(Math.abs(bps) / 100);
  return `${bps > 0 ? '+' : '−'}${pct} %`;
}

// ---------------------------------------------------------------------------
// Líneas y totales del carrito
// ---------------------------------------------------------------------------

/** Importe bruto de una línea: precio unitario × cantidad (milésimas), al centavo. */
export function lineGross(unitPrice: Cents, qtyMilli: number): Cents {
  return Math.round((unitPrice * qtyMilli) / 1000);
}

/** IVA incluido en un importe final: neto + IVA = total. */
export function splitTax(total: Cents, taxBps: number): { net: Cents; tax: Cents } {
  const net = Math.round((total * 10000) / (10000 + taxBps));
  return { net, tax: total - net };
}

/** Porcentaje de un importe, al centavo. */
export function percentOf(cents: Cents, pct: number): Cents {
  return Math.round((cents * pct) / 100);
}

export interface CartLineInput {
  unitPrice: Cents;
  qtyMilli: number;
  /** Descuento propio de la línea, en centavos. */
  discount: Cents;
  taxBps: number;
}

export interface CartLineTotals {
  gross: Cents;
  /** Descuento total de la línea: el propio + su parte del descuento general. */
  discount: Cents;
  total: Cents;
  net: Cents;
  tax: Cents;
}

export interface CartTotals {
  lines: CartLineTotals[];
  subtotal: Cents;
  discountTotal: Cents;
  /** Lo que se cobra antes de los ajustes por medio de pago. */
  baseTotal: Cents;
  taxTotal: Cents;
}

export class SaleError extends Error {}

/**
 * Reparte un importe entre partes proporcionales a `weights`, sin perder centavos
 * (método del resto mayor). La suma del resultado es exactamente `amount`.
 */
export function prorate(amount: Cents, weights: number[]): Cents[] {
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  if (amount === 0 || totalWeight === 0) return weights.map(() => 0);
  const exact = weights.map((w) => (amount * w) / totalWeight);
  const parts = exact.map((x) => Math.floor(x));
  let rest = amount - parts.reduce((a, b) => a + b, 0);
  const order = exact.map((x, i) => ({ i, frac: x - Math.floor(x) })).sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (let k = 0; rest > 0; k = (k + 1) % order.length, rest--) parts[order[k]!.i]! += 1;
  return parts;
}

/**
 * Totales del carrito. El descuento general se reparte entre las líneas (así cada línea
 * sabe cuánto se cobró de verdad: lo necesitan el IVA y las devoluciones).
 */
export function computeCart(lines: CartLineInput[], globalDiscount: Cents = 0): CartTotals {
  if (!Number.isSafeInteger(globalDiscount) || globalDiscount < 0) throw new SaleError('Descuento inválido.');
  const gross = lines.map((l) => {
    if (!Number.isSafeInteger(l.qtyMilli) || l.qtyMilli <= 0) throw new SaleError('Hay cantidades inválidas.');
    if (!Number.isSafeInteger(l.unitPrice) || l.unitPrice < 0) throw new SaleError('Hay precios inválidos.');
    if (!isValidTaxBps(l.taxBps)) throw new SaleError('Alícuota de IVA inválida.');
    const g = lineGross(l.unitPrice, l.qtyMilli);
    if (!Number.isSafeInteger(l.discount) || l.discount < 0 || l.discount > g) throw new SaleError('El descuento de una línea no puede superar su importe.');
    return g;
  });
  const afterOwn = lines.map((l, i) => gross[i]! - l.discount);
  const available = afterOwn.reduce((a, b) => a + b, 0);
  if (globalDiscount > available) throw new SaleError('El descuento no puede superar el total.');
  const shares = prorate(globalDiscount, afterOwn);

  const totals = lines.map((l, i): CartLineTotals => {
    const discount = l.discount + shares[i]!;
    const total = gross[i]! - discount;
    return { gross: gross[i]!, discount, total, ...splitTax(total, l.taxBps) };
  });
  const subtotal = totals.reduce((a, t) => a + t.gross, 0);
  const discountTotal = totals.reduce((a, t) => a + t.discount, 0);
  return {
    lines: totals,
    subtotal,
    discountTotal,
    baseTotal: subtotal - discountTotal,
    taxTotal: totals.reduce((a, t) => a + t.tax, 0),
  };
}

/** Descuento sobre el subtotal, en % (para el tope de descuento del empleado). */
export function discountPct(subtotal: Cents, discountTotal: Cents): number {
  return subtotal > 0 ? (discountTotal / subtotal) * 100 : 0;
}

// ---------------------------------------------------------------------------
// Pagos
// ---------------------------------------------------------------------------

export interface PaymentLine {
  method: PaymentMethod;
  /** Parte del total base que cubre este pago. */
  base: Cents;
  /** Recargo (+) o descuento (−) del medio de pago. */
  adjustment: Cents;
  /** Redondeo del efectivo. */
  rounding: Cents;
  /** Lo que paga el cliente con este medio: base + ajuste + redondeo. */
  amount: Cents;
  /** Efectivo: con cuánto pagó (para el vuelto). */
  tendered: Cents | null;
}

export function adjustmentFor(base: Cents, bps: number): Cents {
  return Math.round((base * bps) / 10000);
}

/** Redondea al múltiplo más cercano de `step` (0 o 1 = sin redondeo). */
export function roundCash(amount: Cents, step: number): Cents {
  if (step <= 1) return amount;
  return Math.round(amount / step) * step;
}

/** Pago que cubre `base` con un medio (aplica su ajuste y, en efectivo, el redondeo). */
export function paymentForBase(method: PaymentMethod, base: Cents, settings: PosSettings): PaymentLine {
  const adjustment = adjustmentFor(base, settings.payments[method].adjust_bps);
  const raw = base + adjustment;
  const amount = method === 'cash' ? roundCash(raw, settings.cash_rounding) : raw;
  return { method, base, adjustment, rounding: amount - raw, amount, tendered: null };
}

/**
 * Pago parcial por un importe (lo que el cliente quiere pagar con ese medio). Se calcula qué
 * parte del total base cubre; si cubre más de lo que falta, se limita a lo que falta.
 */
export function paymentForAmount(method: PaymentMethod, amount: Cents, remainingBase: Cents, settings: PosSettings): PaymentLine {
  const bps = settings.payments[method].adjust_bps;
  const base = Math.round((amount * 10000) / (10000 + bps));
  if (base >= remainingBase) return paymentForBase(method, remainingBase, settings);
  return { method, base, adjustment: amount - base, rounding: 0, amount, tendered: null };
}

export function paymentTotals(payments: PaymentLine[]): { base: Cents; adjustment: Cents; rounding: Cents; total: Cents } {
  return payments.reduce(
    (t, p) => ({ base: t.base + p.base, adjustment: t.adjustment + p.adjustment, rounding: t.rounding + p.rounding, total: t.total + p.amount }),
    { base: 0, adjustment: 0, rounding: 0, total: 0 },
  );
}

/** Vuelto de los pagos en efectivo (0 si no se indicó con cuánto pagó). */
export function changeDue(payments: PaymentLine[]): Cents {
  return payments.reduce((sum, p) => sum + (p.method === 'cash' && p.tendered != null ? Math.max(0, p.tendered - p.amount) : 0), 0);
}

// ---------------------------------------------------------------------------
// Devoluciones
// ---------------------------------------------------------------------------

/**
 * Cuánto se devuelve de una línea. Se calcula sobre lo acumulado para que varias devoluciones
 * parciales sumen exactamente el total de la línea (sin perder ni regalar centavos).
 */
export function refundForLine(lineTotal: Cents, lineQty: number, alreadyReturned: number, returnQty: number): Cents {
  if (returnQty <= 0 || alreadyReturned + returnQty > lineQty) throw new SaleError('No se puede devolver más de lo vendido.');
  const upTo = (q: number) => Math.round((lineTotal * q) / lineQty);
  return upTo(alreadyReturned + returnQty) - upTo(alreadyReturned);
}

/**
 * Parte proporcional de los recargos/descuentos por medio de pago (y redondeo) de la venta
 * original que corresponde devolver, también acumulada.
 */
export function refundAdjustment(originalBase: Cents, originalAdjustments: Cents, alreadyRefundedBase: Cents, refundBase: Cents): Cents {
  if (originalBase <= 0) return 0;
  const upTo = (b: number) => Math.round((originalAdjustments * b) / originalBase);
  return upTo(alreadyRefundedBase + refundBase) - upTo(alreadyRefundedBase);
}

/** "0001-00000123": código del dispositivo + correlativo. */
export function formatSaleNumber(deviceCode: number, seq: number): string {
  return `${String(deviceCode).padStart(4, '0')}-${String(seq).padStart(8, '0')}`;
}
